import { Button, Center, Group, Loader, Pagination, Select, Text, TextInput, Title } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { showNotification } from '@mantine/notifications';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exportPaginatedData,
  generateCSV,
  getTagGroups,
  MessageHistoryParams,
  messagesHistoryList,
  useBot,
  useGetFeedbackCheckbox,
  useDateLocal,
  cleanAgentContent,
} from '@mygpt/app';
import { MessageDetailInfo } from '@mygpt/types';
import type { QuestionTagGroupOut } from '@mygpt/types/tag';

import { UnansweredTableItem, FeedbackTable } from './FeedbackTable';
import { TagOverflowCounterMultiSelect } from '@/components/OverflowCounterMultiSelect';
import { RatingData } from '@/components/TeachModel/FeedbackTable';
import { useDebouncedCallback } from '@mantine/hooks';
import { groupTagsByGroup } from '@/components/MessageHistory/util';
import { getRatingReasonText, getRatingType } from '@/utils/feedback';
import { confirmPartialExport } from '@/utils/exportConfirm';

const DEFAULT_PAGE_DATA = {
  page: 1,
  total: 0,
  size: 50,
  pages: 1,
};

type FeedbackType = 'all' | 'good' | 'bad';

interface Page {
  page: number;
  total: number;
  size: number;
  pages: number;
}

dayjs.extend(utc);

const MessageHistory = ({
  dateRange,
  onDateRangeChange,
  setRangeDateTimeText,
}: {
  dateRange: DatesRangeValue;
  onDateRangeChange: (date: DatesRangeValue) => void;
  setRangeDateTimeText: (text: string) => void;
}) => {
  const { t } = useTranslation();
  const { botId, robotInfo } = useBot();
  const { dateLocale } = useDateLocal();
  // const [] =

  const tabelDataRef = useRef<MessageDetailInfo[] | null>(null);
  const feedbackCheckboxs = useGetFeedbackCheckbox();
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [tableData, setTableData] = useState<UnansweredTableItem[]>([]);
  const [pageData, setPageData] = useState<Page>(DEFAULT_PAGE_DATA);

  const [typeValue, setTypeValue] = useState<string>('all');
  const [feedbackValue, setFeedbackValue] = useState<FeedbackType>('all');
  const [keyword, setKeyword] = useState<{ value: string; isComposing: boolean }>({ value: '', isComposing: false });
  const [userIdentifier, setUserIdentifier] = useState<string>('');
  const [urlKeyword, setUrlKeyword] = useState<string>('');
  const [userIdentifierList, setUserIdentifierList] = useState<{ value: string; label: string }[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>();

  const [tagGroups, setTagGroups] = useState<QuestionTagGroupOut[]>([]);
  const [loadingTagGroups, setLoadingTagGroups] = useState(false);

  const TypeData = [
    { value: 'all', label: t('chat_history.source_type.all') },
    { value: 'playground', label: t('chat_history.source_type.playground') },
    { value: 'share', label: t('chat_history.source_type.share') },
    { value: 'widget', label: t('chat_history.source_type.widget') },
    { value: 'api', label: t('chat_history.source_type.api') },
    { value: 'realtime', label: t('chat_history.source_type.realtime') },
  ];

  const FeedbackData = [
    { value: 'all', label: t('teach_model.feedback_type.all') },
    { value: 'good', label: t('teach_model.feedback_type.well') },
    { value: 'bad', label: t('teach_model.feedback_type.poorly') },
  ];

  const getSourceLabel = (source: string) => {
    return TypeData.find((_) => _.value === source)?.label;
  };

  const convertTableData = (data: MessageDetailInfo[]): UnansweredTableItem[] => {
    const table: UnansweredTableItem[] = [];
    data.map((item) => {
      const rating: RatingData[] = [];
      const type = getRatingType(item.rating);

      if (type) {
        if (item.session_message_metadata?.length) {
          item.session_message_metadata?.map((meta) => {
            if (meta.key !== 'value') return;
            if (!meta.value) return;
            let value: unknown;
            try {
              value = JSON.parse(meta.value.replace(/'/g, '"'));
            } catch (e) {
              console.warn('Failed to parse session_message_metadata, skipped:', meta.value, e);
              return;
            }
            if (!Array.isArray(value)) return;
            value.map((metaValue: string) => {
              rating.push({
                type,
                text: metaValue,
              });
            });
            if (!rating.length) {
              rating.push({
                type,
                text: '',
              });
            }
          });
        } else {
          rating.push({
            type,
            text: '',
          });
        }
      }

      table.push({
        id: item.id,
        faqId: item.faq_id,
        citation: item.citation || [],
        time: dayjs(item.created_at).valueOf(),
        question: item.question,
        answer: item.answer,
        rating: rating,
        detail: item.feedback_content || '',
        session: item.session,
        tags: item.tags || [],
        transfer_to_human: item.transfer_to_human,
        url: item.url || '',
      });
    });
    return table;
  };

  const getSessionMessageListParams = (customParam?: Partial<MessageHistoryParams>) => {
    const params = {
      aiId: botId,
      page: pageData.page,
      size: pageData.size,
      unanswerable: true,
      tags: filterTags,
    } as MessageHistoryParams;

    if (typeValue !== 'all') {
      params.source = typeValue as 'widget' | 'api' | 'playground' | 'share' | 'realtime';
    }

    if (feedbackValue !== 'all') {
      params.feedback_type = feedbackValue as 'good' | 'bad';
    }

    if (keyword.value && !keyword.isComposing) {
      params.keyword = keyword.value;
    }

    if (dateRange[0] && dateRange[1]) {
      params.start_time = dayjs(dateRange[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      params.end_time = dayjs(dateRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    }

    if (userIdentifier) {
      params.user_identifier = userIdentifier;
    }

    if (urlKeyword.trim()) {
      params.url_keyword = urlKeyword.trim();
    }

    if (customParam) {
      Object.assign(params, customParam);
    }

    return params;
  };

  const init = async (showLoading = true) => {
    if (showLoading) {
      setTableData([]);
      setTableLoading(true);
    }

    if (keyword.isComposing) return;

    const params = getSessionMessageListParams();

    await messagesHistoryList(params)
      .then((res) => {
        const data = res.data;
        const { items, page, pages, size, total } = data;

        setPageData({ page, pages, total, size });

        if (!items || !Array.isArray(items)) return;
        tabelDataRef.current = items;
        const table = convertTableData(items);
        setTableData(table);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const handleDebouncedInit = useDebouncedCallback(init, 700);

  const isTagValid = (name: string) => {
    return tagGroups.some((group) => group.tags.some((tag) => tag.name === name));
  };

  const downloadByRating = async () => {
    setDownloadLoading(true);

    try {
      const params = getSessionMessageListParams();
      const result = await exportPaginatedData<MessageDetailInfo>({
        fetchPage: async (page, size) => {
          const res = await messagesHistoryList({ ...params, page, size });
          return res?.data;
        },
        onConfirmPartial: (fetched, total) => confirmPartialExport(t, fetched, total),
      });

      if (!result) return;

      if (result.partial) {
        showNotification({
          message: t('chat_history.export.partial_success', {
            fetched: result.items.length,
            total: result.total,
          }),
          color: 'yellow.5',
        });
      }

      const tableData = convertTableData(result.items);

      const time =
        dateRange[0] && dateRange[1]
          ? ` ${dayjs(dateRange[0]).format('YYYY.MM.DD')}_${dayjs(dateRange[1]).format('YYYY.MM.DD')}`
          : '';

      const fileName = `${t('chat_history.tab.message_history')}_${time}`;
      const header = [
        t('chat_history.table.created_at'),
        t('teach_model.table.question'),
        t('faqs.form.answer'),
        t('teach_model.user_feedback'),
        t('teach_model.table.rating'),
        t('teach_model.table.feedback_details'),
        t('chat_history.table.type'),
        t('chat_history.table.source_url'),
        t('tag.tags'),
        t('chat_history.message_detail.session_id'),
        t('chat_history.table.user'),
        t('settings.feedback.has_feedback'),
      ];

      const rows = tableData.map((item) => {
        // 清理答案中的 agent 特殊标签
        const answer = cleanAgentContent(item.answer || '');

        return [
          dayjs(item.time).format('YYYY-MM-DD HH:mm:ss'),
          item.question || '',
          answer,
          item.rating.length
            ? item.rating[0].type === 'good'
              ? t('teach_model.feedback_type.well')
              : t('teach_model.feedback_type.poorly')
            : '-',
          getRatingReasonText(item.rating, feedbackCheckboxs),
          item.detail || '-',
          getSourceLabel(item.session.source) || '-',
          item.url || '-',
          Object.entries(groupTagsByGroup(item.tags))
            .map(([_, group]) => {
              const tagTexts = group.tags.map((tag) => {
                const tagText = tag.name;
                return isTagValid(tag.name) ? tagText : `${tagText}${t('tag.tag_deleted')}`;
              });
              return `[${group.groupName}]: ${tagTexts.join('、')}`;
            })
            .join('\n') || '-',
          item.session.session_id,
          item.session.user_identifier || '-',
          item.transfer_to_human ? '✓' : '',
        ];
      });
      const csvData = {
        header,
        rows,
      };

      generateCSV(fileName, csvData);
    } catch (error) {
      console.error('批量获取数据失败:', error);
      showNotification({
        message: t('export_failed'),
        color: 'red.5',
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const fetchTagGroups = async () => {
    if (!botId) return;

    setLoadingTagGroups(true);
    try {
      const { data: tagGroupsData } = await getTagGroups(botId);
      setTagGroups(tagGroupsData);
    } catch (error) {
      console.error('获取标签组失败:', error);
    } finally {
      setLoadingTagGroups(false);
    }
  };

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      handleDebouncedInit();
    }
  }, [pageData.page, pageData.size, typeValue, feedbackValue, keyword, userIdentifier, filterTags, urlKeyword]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      if (pageData.page === 1) {
        handleDebouncedInit();
      } else {
        setPageData({ page: 1, pages: pageData.pages, total: pageData.total, size: pageData.size });
      }
    }
  }, [dateRange]);

  useEffect(() => {
    if (tabelDataRef.current) {
      const table = convertTableData(tabelDataRef.current);
      setTableData(table);
    }
  }, [t]);

  useEffect(() => {
    if (botId) {
      fetchTagGroups();
    }
  }, [botId]);

  const getUserIdentifierList = () => {
    const params = getSessionMessageListParams({ page: 1, size: 1000 });
    messagesHistoryList(params).then((res) => {
      const data = res.data;
      const { items } = data;
      if (!items || !Array.isArray(items)) return;
      const list = new Set(
        items.filter((item) => Boolean(item.session.user_identifier)).map((item) => item.session.user_identifier)
      );
      setUserIdentifierList(Array.from(list).map((item) => ({ value: item, label: item })));
    });
  };

  useEffect(() => {
    getUserIdentifierList();
  }, []);

  return (
    <div>
      <Group justify="space-between" mb={20}>
        <Title order={4} fw={500}>
          {t('chat_history.tab.message_history')}
        </Title>
        <Group>
          <TextInput
            size="sm"
            style={{ width: 200 }}
            value={keyword.value}
            onChange={(e) => {
              setKeyword({ value: e.target.value, isComposing: keyword.isComposing || false });
              setPageData((prev) => ({ ...prev, page: DEFAULT_PAGE_DATA.page, size: DEFAULT_PAGE_DATA.size }));
            }}
            onCompositionStart={() => setKeyword({ value: keyword.value, isComposing: true })}
            onCompositionEnd={() => setKeyword({ value: keyword.value, isComposing: false })}
            placeholder={t('chat_history.tab.search_placeholder')}
          />
          <TextInput
            size="sm"
            style={{ width: 200 }}
            value={urlKeyword}
            onChange={(e) => {
              setUrlKeyword(e.target.value);
              setPageData((prev) => ({ ...prev, page: DEFAULT_PAGE_DATA.page, size: DEFAULT_PAGE_DATA.size }));
            }}
            placeholder={t('chat_history.tab.url_placeholder')}
          />
          <TagOverflowCounterMultiSelect
            placeholder={t('tag.select_tags')}
            data={tagGroups.map((group) => ({
              group: group.name,
              items: group.tags.map((tag) => tag.name),
            }))}
            value={filterTags || []}
            onChange={(value) => {
              setFilterTags(value);
            }}
            searchable
            maxDropdownHeight={200}
            maxVisibleItems={5}
          />
          <Select
            size="sm"
            style={{ width: 120 }}
            value={userIdentifier}
            onChange={(value: string | null) => {
              setUserIdentifier(value || '');
            }}
            data={[{ value: '', label: t('chat_history.user_identifier.all') }, ...userIdentifierList]}
            searchable
          />
          <Select
            size="sm"
            style={{ width: 120 }}
            value={typeValue}
            onChange={(value: string | null) => {
              setTypeValue(value || 'all');
              setPageData((prev) => ({ ...prev, page: DEFAULT_PAGE_DATA.page, size: DEFAULT_PAGE_DATA.size }));
            }}
            data={TypeData}
            allowDeselect={false}
          />
          <Select
            size="sm"
            style={{ width: 120 }}
            value={feedbackValue}
            onChange={(value) => {
              setFeedbackValue((value as FeedbackType) || 'all');
              setPageData((prev) => ({ ...prev, page: DEFAULT_PAGE_DATA.page, size: DEFAULT_PAGE_DATA.size }));
            }}
            data={FeedbackData}
            allowDeselect={false}
          />
          <DatePickerInput
            type="range"
            locale={dateLocale}
            size="sm"
            value={dateRange}
            onChange={(value) => {
              onDateRangeChange(value);
              setRangeDateTimeText(t('dashboard.date.custom_date'));
              setPageData((prev) => ({ ...prev, page: DEFAULT_PAGE_DATA.page, size: DEFAULT_PAGE_DATA.size }));
            }}
            valueFormat="YYYY-MM-DD"
            numberOfColumns={2}
            allowSingleDateInRange
          />

          <Button loading={downloadLoading} disabled={!tableLoading && !tableData.length} onClick={downloadByRating}>
            {t('teach_model.export_log')}
          </Button>
        </Group>
      </Group>

      <FeedbackTable
        data={tableData}
        tagGroups={tagGroups}
        loadingTagGroups={loadingTagGroups}
        onRefreshTagGroups={fetchTagGroups}
        actionCallBack={() => {
          init(false);
        }}
      />

      {tableLoading && (
        <Center className="h-[300px] bg-white flex items-center">
          <Loader />
        </Center>
      )}

      {!tableLoading && !tableData.length && (
        <Center className="h-[300px] bg-white flex items-center">
          <Text c="gray.5">{t('no_data')}</Text>
        </Center>
      )}

      {!!pageData.total && (
        <Group justify="space-between" mt={15}>
          <Text size={'sm'} c="gray.5">
            {t('teach_model.table.total_items', { number: pageData.total })}
          </Text>
          <Pagination
            value={pageData.page}
            size={'sm'}
            onChange={(value) => {
              setPageData((state) => ({ ...state, page: value }));
            }}
            total={pageData.pages}
            color="violet.5"
          />
        </Group>
      )}
    </div>
  );
};

export default MessageHistory;
